import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { ChevronDown, CheckCircle2, Users, Award, Heart, Sparkles, Zap, Target } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const Home = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const { toast } = useToast();
  const observerRefs = useRef({});

  // Animated counter effect
  useEffect(() => {
    const existingEmails = JSON.parse(localStorage.getItem('vestaWaitlist') || '[]');
    const targetCount = 200 + existingEmails.length;
    let currentCount = 0;
    const increment = Math.ceil(targetCount / 50);
    
    const timer = setInterval(() => {
      currentCount += increment;
      if (currentCount >= targetCount) {
        setWaitlistCount(targetCount);
        clearInterval(timer);
      } else {
        setWaitlistCount(currentCount);
      }
    }, 30);

    return () => clearInterval(timer);
  }, []);

  // Intersection Observer for reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    Object.values(observerRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const response = await fetch(`${API}/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          timestamp: new Date().toISOString()
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Update counter
        const existingEmails = JSON.parse(localStorage.getItem('vestaWaitlist') || '[]');
        existingEmails.push({ email, timestamp: new Date().toISOString() });
        localStorage.setItem('vestaWaitlist', JSON.stringify(existingEmails));
        setWaitlistCount(500 + existingEmails.length);

        toast({
          title: "You're on the list! 🎉",
          description: "Welcome to the Vesta crew. Get ready for something epic.",
        });

        setEmail('');
      } else {
        toast({
          title: "Oops!",
          description: data.message || "Something went wrong. Please try again.",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Connection Error",
        description: "Unable to submit. Please check your connection and try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Bold & Modern */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b-4 border-[#004D51]">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <a href="/" className="flex items-center">
            <img 
              src="/vesta-secondary-teal-flame.png" 
              alt="Vesta Workwear" 
              className="h-10 w-auto"
            />
          </a>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#about" className="text-[#545759] hover:text-[#DB5A14] transition-colors font-bold text-sm uppercase tracking-wide">About</a>
            <a href="#mission" className="text-[#545759] hover:text-[#DB5A14] transition-colors font-bold text-sm uppercase tracking-wide">Mission</a>
            <a href="#community" className="text-[#545759] hover:text-[#DB5A14] transition-colors font-bold text-sm uppercase tracking-wide">Community</a>
            <a href="#faq" className="text-[#545759] hover:text-[#DB5A14] transition-colors font-bold text-sm uppercase tracking-wide">FAQ</a>
          </nav>
          <Button 
            className="bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black px-8 py-2 rounded-lg transform hover:scale-105 transition-all"
            onClick={() => document.getElementById('waitlist-form')?.scrollIntoView({ behavior: 'smooth' })}
          >
            JOIN NOW
          </Button>
        </div>
      </header>

      {/* Hero Section - Retro Modern Vibes */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background with overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#004D51] via-[#003338] to-[#001f22]"></div>
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1658806312303-6815563cd9d8)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.1
          }}></div>
          
          {/* Static geometric shapes - improved accessibility */}
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#DB5A14] opacity-20 blur-3xl"></div>
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#F39C49] opacity-15 blur-3xl"></div>
          
          {/* Retro grid lines */}
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: 'linear-gradient(#F39C49 1px, transparent 1px), linear-gradient(90deg, #F39C49 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="container mx-auto px-6 z-10 text-center">
          <div className="max-w-5xl mx-auto space-y-8">
            {/* Edgy badge */}
            <div className="inline-block">
              <div className="px-6 py-2 bg-[#DB5A14] text-white font-bold text-sm tracking-wider transform -rotate-1 shadow-lg">
                PRE-LAUNCH // AUSTRALIA
              </div>
            </div>

            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white leading-none tracking-tight">
              <span className="block">Built by <span className="text-[#F39C49]">Women</span>.</span>
              <span className="block">For <span className="text-[#F39C49]">Women</span>.</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-[#CFD2D4] font-medium max-w-2xl mx-auto leading-relaxed">
              Workwear engineered for the women who <span className="text-[#F39C49] font-bold">create</span>, <span className="text-[#F39C49] font-bold">craft</span>, and <span className="text-[#F39C49] font-bold">belong</span> in the automotive space.
            </p>

            {/* Waitlist Counter - Animated */}
            <div className="py-4">
              <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-md px-8 py-3 rounded-full border-2 border-[#F39C49]/30">
                <Sparkles className="w-5 h-5 text-[#F39C49] animate-pulse" />
                <span className="text-3xl font-black text-white">{waitlistCount}</span>
                <span className="text-[#CFD2D4] font-medium">women ready to make history</span>
              </div>
            </div>

            {/* Waitlist Form */}
            <form id="waitlist-form" onSubmit={handleWaitlistSubmit} className="max-w-md mx-auto mt-12">
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1 h-16 px-6 text-lg bg-white/95 border-2 border-[#CFD2D4] focus:border-[#F39C49] rounded-lg font-medium"
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="h-16 px-10 bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black text-lg rounded-lg transition-all duration-300 transform hover:scale-105 hover:rotate-1 shadow-xl"
                >
                  {isSubmitting ? 'JOINING...' : "LET'S GO"}
                </Button>
              </div>
              <p className="text-sm text-[#CFD2D4] mt-3 font-medium">
                ⚡ Get early access + exclusive launch deals
              </p>
            </form>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
            <ChevronDown className="w-8 h-8 text-white/60" />
          </div>
        </div>
      </section>

      {/* About Vesta Workwear - Reveal Animation */}
      <section 
        id="about" 
        ref={(el) => (observerRefs.current.about = el)}
        className={`py-24 bg-white transition-all duration-1000 ${isVisible.about ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      >
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-block mb-4">
                <div className="px-4 py-2 bg-[#DB5A14] text-white font-bold text-xs tracking-widest transform rotate-1">
                  OUR STORY
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-black text-[#004D51] leading-tight">
                Built by <span className="text-[#DB5A14]">Women</span>.<br />
                For <span className="text-[#DB5A14]">Women</span>.
              </h2>
              <p className="text-lg text-[#545759] leading-relaxed font-medium">
                We're not just making workwear—we're creating a <span className="font-bold text-[#004D51]">movement</span>. Vesta is for the painters, the refinishers, the artists who transform metal into masterpieces every single day.
              </p>
              <p className="text-lg text-[#545759] leading-relaxed">
                No more squeezing into gear made for men. No more compromising safety for fit. We're engineering workwear that celebrates your craft while keeping you protected, comfortable, and looking absolute fire.
              </p>
              <div className="pt-6 flex flex-wrap gap-4">
                <div className="px-6 py-3 bg-[#F3CEB2] rounded-lg border-2 border-[#004D51]">
                  <span className="text-[#004D51] font-black text-sm">🇦🇺 AUSSIE OWNED</span>
                </div>
                <div className="px-6 py-3 bg-[#004D51] text-white rounded-lg font-black text-sm">
                  FEMALE-FOUNDED
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-full h-full bg-[#F39C49] rounded-2xl opacity-20 transform rotate-3"></div>
              <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-300">
                <img
                  src="/automotive-1.jpg"
                  alt="Professional woman in automotive industry"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#004D51] to-transparent">
                  <p className="text-white font-bold text-lg">Real craftsmanship. Real protection.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why We Exist - Bold & Spirited */}
      <section 
        id="mission" 
        ref={(el) => (observerRefs.current.mission = el)}
        className={`py-24 bg-[#F3CEB2]/30 transition-all duration-1000 ${isVisible.mission ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      >
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black text-[#004D51] mb-4">
              This Is <span className="text-[#DB5A14]">More Than</span> Workwear
            </h2>
            <p className="text-xl text-[#545759] max-w-3xl mx-auto font-medium">
              It's a statement. A revolution. A way to show the world that women in trades aren't just here—<span className="font-bold text-[#004D51]">we're thriving</span>.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 bg-white border-4 border-[#004D51] shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-3 hover:rotate-1 group">
              <div className="w-20 h-20 bg-gradient-to-br from-[#004D51] to-[#003338] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Target className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-black text-[#004D51] mb-4">MADE FOR YOU</h3>
              <p className="text-[#545759] leading-relaxed font-medium">
                Engineered for women's bodies. No more swimming in oversized gear or dealing with awkward fits. This is workwear that actually <span className="font-bold">works</span>.
              </p>
            </Card>

            <Card className="p-8 bg-white border-4 border-[#DB5A14] shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-3 hover:rotate-1 group">
              <div className="w-20 h-20 bg-gradient-to-br from-[#DB5A14] to-[#c54f10] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Zap className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-black text-[#004D51] mb-4">BUILT TOUGH</h3>
              <p className="text-[#545759] leading-relaxed font-medium">
                Premium materials that stand up to spray guns, paint booths, and whatever your craft throws at you. Safety meets style. Finally.
              </p>
            </Card>

            <Card className="p-8 bg-white border-4 border-[#F39C49] shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-3 hover:rotate-1 group">
              <div className="w-20 h-20 bg-gradient-to-br from-[#F39C49] to-[#DB5A14] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Heart className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-black text-[#004D51] mb-4">BY THE CREW</h3>
              <p className="text-[#545759] leading-relaxed font-medium">
                Designed with real input from women in the industry. This isn't corporate guesswork—it's made by people who get it.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Community & Values - Energetic */}
      <section 
        id="community" 
        ref={(el) => (observerRefs.current.community = el)}
        className={`py-24 bg-white transition-all duration-1000 ${isVisible.community ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      >
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <div className="px-6 py-2 bg-[#F39C49] text-white font-black text-sm tracking-widest transform -rotate-2">
                THE VESTA CREW
              </div>
            </div>
            <h2 className="text-5xl md:text-6xl font-black text-[#004D51] mb-4">
              You're Not <span className="text-[#DB5A14]">Alone</span>
            </h2>
            <p className="text-xl text-[#545759] max-w-3xl mx-auto font-medium">
              Join hundreds of women who are redefining what it means to work in automotive refinishing. We're louder together.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="relative h-80 rounded-2xl overflow-hidden shadow-2xl group transform hover:-rotate-1 transition-all duration-300">
              <img
                src="/automotive-2.jpg"
                alt="Women collaborating in trades"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#004D51] via-[#004D51]/50 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <h3 className="text-3xl font-black text-white mb-2">STRENGTH IN NUMBERS</h3>
                <p className="text-[#CFD2D4] font-medium">Empowering every woman who picks up a spray gun</p>
              </div>
            </div>

            <div className="relative h-80 rounded-2xl overflow-hidden shadow-2xl group transform hover:rotate-1 transition-all duration-300">
              <img
                src="/automotive-3.jpg"
                alt="Women in automotive refinishing"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#DB5A14] via-[#DB5A14]/50 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <h3 className="text-3xl font-black text-white mb-2">CRAFTED TOGETHER</h3>
                <p className="text-white font-medium">Your voice shapes what we create</p>
              </div>
            </div>
          </div>

          {/* Values - Retro Style */}
          <div className="relative bg-[#004D51] rounded-3xl p-12 overflow-hidden">
            {/* Retro stripes background */}
            <div className="absolute inset-0 opacity-5" style={{
              backgroundImage: 'repeating-linear-gradient(45deg, #F39C49 0, #F39C49 10px, transparent 10px, transparent 20px)'
            }}></div>
            
            <div className="relative z-10">
              <h3 className="text-4xl font-black text-white mb-2 text-center">WHAT WE STAND FOR</h3>
              <p className="text-[#F39C49] text-center font-bold mb-12">NO COMPROMISES. EVER.</p>
              
              <div className="grid md:grid-cols-4 gap-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border-2 border-[#F39C49] transform hover:scale-105 transition-transform">
                  <div className="text-3xl font-black text-[#F39C49] mb-3">01</div>
                  <div className="text-white font-black text-xl mb-2">INCLUSIVITY</div>
                  <p className="text-[#CFD2D4] text-sm font-medium">Every woman deserves gear that fits right</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border-2 border-[#F39C49] transform hover:scale-105 transition-transform">
                  <div className="text-3xl font-black text-[#F39C49] mb-3">02</div>
                  <div className="text-white font-black text-xl mb-2">INNOVATION</div>
                  <p className="text-[#CFD2D4] text-sm font-medium">Pushing what's possible in workwear</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border-2 border-[#F39C49] transform hover:scale-105 transition-transform">
                  <div className="text-3xl font-black text-[#F39C49] mb-3">03</div>
                  <div className="text-white font-black text-xl mb-2">SAFETY FIRST</div>
                  <p className="text-[#CFD2D4] text-sm font-medium">Protection you can count on, always</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border-2 border-[#F39C49] transform hover:scale-105 transition-transform">
                  <div className="text-3xl font-black text-[#F39C49] mb-3">04</div>
                  <div className="text-white font-black text-xl mb-2">COMMUNITY</div>
                  <p className="text-[#CFD2D4] text-sm font-medium">We rise by lifting each other</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist CTA Section - High Energy */}
      <section className="py-24 bg-gradient-to-br from-[#DB5A14] via-[#c54f10] to-[#004D51] text-white relative overflow-hidden">
        <div className="absolute inset-0">
          {/* Animated geometric shapes */}
          <div className="absolute top-10 left-10 w-64 h-64 bg-[#F39C49] rounded-full opacity-20 blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full opacity-10 blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          
          {/* Retro diagonal stripes */}
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: 'repeating-linear-gradient(-45deg, white 0, white 2px, transparent 2px, transparent 20px)'
          }}></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="inline-block mb-6">
              <div className="px-8 py-3 bg-white text-[#DB5A14] font-black text-xl transform -rotate-2 shadow-xl">
                ⚡ SECURE YOUR SPOT ⚡
              </div>
            </div>
            
            <h2 className="text-5xl md:text-7xl font-black leading-tight">
              DON'T MISS<br />THE <span className="text-[#F3CEB2]">DROP</span>
            </h2>
            <p className="text-2xl text-white font-bold max-w-2xl mx-auto">
              First dibs. Exclusive deals. Behind-the-scenes access. You in?
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 py-8">
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border-2 border-white/30 transform hover:scale-105 transition-transform">
                <CheckCircle2 className="w-12 h-12 text-[#F3CEB2] mx-auto mb-3" />
                <p className="font-black text-lg mb-2">EARLY ACCESS</p>
                <p className="text-sm text-white/90">Shop before anyone else</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border-2 border-white/30 transform hover:scale-105 transition-transform">
                <CheckCircle2 className="w-12 h-12 text-[#F3CEB2] mx-auto mb-3" />
                <p className="font-black text-lg mb-2">VIP UPDATES</p>
                <p className="text-sm text-white/90">Sneak peeks & exclusive content</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border-2 border-white/30 transform hover:scale-105 transition-transform">
                <CheckCircle2 className="w-12 h-12 text-[#F3CEB2] mx-auto mb-3" />
                <p className="font-black text-lg mb-2">LAUNCH DEALS</p>
                <p className="text-sm text-white/90">Waitlist-only discounts</p>
              </div>
            </div>

            <form onSubmit={handleWaitlistSubmit} className="max-w-lg mx-auto mt-8">
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1 h-16 px-6 text-lg bg-white text-[#004D51] border-4 border-white rounded-xl font-bold"
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="h-16 px-10 bg-white text-[#DB5A14] hover:bg-[#F3CEB2] font-black text-lg rounded-xl transition-all duration-300 transform hover:scale-105"
                >
                  {isSubmitting ? 'JOINING...' : "I'M IN!"}
                </Button>
              </div>
            </form>

            <div className="pt-6 flex items-center justify-center space-x-4">
              <Sparkles className="w-6 h-6 text-[#F3CEB2] animate-pulse" />
              <span className="text-white font-black text-lg">LAUNCHING SOON</span>
              <Sparkles className="w-6 h-6 text-[#F3CEB2] animate-pulse" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section 
        id="faq" 
        ref={(el) => (observerRefs.current.faq = el)}
        className={`py-24 bg-white transition-all duration-1000 ${isVisible.faq ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      >
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block mb-4">
                <div className="px-6 py-2 bg-[#004D51] text-white font-black text-sm tracking-widest">
                  GOT QUESTIONS?
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-black text-[#004D51]">We've Got <span className="text-[#DB5A14]">Answers</span></h2>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="border-4 border-[#004D51] rounded-xl px-6 bg-white shadow-lg hover:shadow-xl transition-shadow">
                <AccordionTrigger className="text-lg font-black text-[#004D51] hover:text-[#DB5A14] py-4">
                  When does Vesta Workwear launch?
                </AccordionTrigger>
                <AccordionContent className="text-[#545759] pb-4 font-medium">
                  We're putting the final touches on something epic! Join the waitlist to be first in line when we drop. Waitlist crew gets VIP early access before anyone else.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="border-4 border-[#DB5A14] rounded-xl px-6 bg-white shadow-lg hover:shadow-xl transition-shadow">
                <AccordionTrigger className="text-lg font-black text-[#004D51] hover:text-[#DB5A14] py-4">
                  What products are coming?
                </AccordionTrigger>
                <AccordionContent className="text-[#545759] pb-4 font-medium">
                  Can't spill all the details yet, but we're creating workwear specifically for women in automotive refinishing. Think: premium materials, real protection, and fits that actually work. It's going to be fire.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="border-4 border-[#F39C49] rounded-xl px-6 bg-white shadow-lg hover:shadow-xl transition-shadow">
                <AccordionTrigger className="text-lg font-black text-[#004D51] hover:text-[#DB5A14] py-4">
                  Why is Vesta different?
                </AccordionTrigger>
                <AccordionContent className="text-[#545759] pb-4 font-medium">
                  We're the ONLY brand designing high-performance workwear specifically for women in automotive refinishing. No more settling. No more compromising. Just gear that gets you.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="border-4 border-[#004D51] rounded-xl px-6 bg-white shadow-lg hover:shadow-xl transition-shadow">
                <AccordionTrigger className="text-lg font-black text-[#004D51] hover:text-[#DB5A14] py-4">
                  How do I become a Vesta ambassador?
                </AccordionTrigger>
                <AccordionContent className="text-[#545759] pb-4 font-medium">
                  We're building an epic ambassador program! Join the waitlist and follow our socials for the inside scoop on how to rep the brand.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="border-4 border-[#DB5A14] rounded-xl px-6 bg-white shadow-lg hover:shadow-xl transition-shadow">
                <AccordionTrigger className="text-lg font-black text-[#004D51] hover:text-[#DB5A14] py-4">
                  Will you ship internationally?
                </AccordionTrigger>
                <AccordionContent className="text-[#545759] pb-4 font-medium">
                  We're Aussie-based but exploring worldwide shipping! Join the waitlist and tell us where you're at—it helps us figure out where to ship first.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="border-4 border-[#F39C49] rounded-xl px-6 bg-white shadow-lg hover:shadow-xl transition-shadow">
                <AccordionTrigger className="text-lg font-black text-[#004D51] hover:text-[#DB5A14] py-4">
                  What makes Vesta premium?
                </AccordionTrigger>
                <AccordionContent className="text-[#545759] pb-4 font-medium">
                  Top-tier materials. Technical construction. Built to last through spray booths, paint jobs, and everything in between. This is investment-level gear that performs.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Footer - Bold */}
      <footer className="bg-[#004D51] text-white py-16 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'repeating-linear-gradient(0deg, #F39C49 0, #F39C49 1px, transparent 1px, transparent 30px), repeating-linear-gradient(90deg, #F39C49 0, #F39C49 1px, transparent 1px, transparent 30px)'
        }}></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <img 
                src="/vesta-logo-footer-white.png" 
                alt="Vesta Workwear" 
                className="h-20 w-auto mb-6"
              />
              <p className="text-[#CFD2D4] mb-4 max-w-md font-medium text-lg">
                Power. Precision. Protection. Workwear built for women who create, craft, and conquer in automotive refinishing.
              </p>
              <div className="flex space-x-4 mt-6">
                <a href="#" className="w-12 h-12 bg-[#DB5A14] hover:bg-[#F39C49] rounded-lg flex items-center justify-center transition-all transform hover:scale-110">
                  <span className="sr-only">Instagram</span>
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href="#" className="w-12 h-12 bg-[#DB5A14] hover:bg-[#F39C49] rounded-lg flex items-center justify-center transition-all transform hover:scale-110">
                  <span className="sr-only">Facebook</span>
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="#" className="w-12 h-12 bg-[#DB5A14] hover:bg-[#F39C49] rounded-lg flex items-center justify-center transition-all transform hover:scale-110">
                  <span className="sr-only">LinkedIn</span>
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
              </div>
            </div>

            <div>
              <h3 className="font-black text-xl mb-4 text-[#F39C49]">QUICK LINKS</h3>
              <ul className="space-y-3 text-[#CFD2D4]">
                <li><a href="#about" className="hover:text-[#F39C49] transition-colors font-medium">About Us</a></li>
                <li><a href="#mission" className="hover:text-[#F39C49] transition-colors font-medium">Our Mission</a></li>
                <li><a href="#community" className="hover:text-[#F39C49] transition-colors font-medium">Community</a></li>
                <li><a href="#faq" className="hover:text-[#F39C49] transition-colors font-medium">FAQ</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-black text-xl mb-4 text-[#F39C49]">GET IN TOUCH</h3>
              <ul className="space-y-3 text-[#CFD2D4]">
                <li>
                  <a href="mailto:hello@vestaworkwear.com.au" className="hover:text-[#F39C49] transition-colors font-medium">
                    hello@vestaworkwear.com.au
                  </a>
                </li>
                <li className="pt-4">
                  <a href="#" className="hover:text-[#F39C49] transition-colors font-medium">Privacy Policy</a>
                </li>
                <li>
                  <a href="#" className="hover:text-[#F39C49] transition-colors font-medium">Terms of Service</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t-4 border-[#DB5A14] pt-8 mt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-[#CFD2D4] font-medium mb-4 md:mb-0">
                © 2025 Vesta Workwear. All rights reserved.
              </p>
              <p className="text-white font-black text-sm tracking-wider">
                🇦🇺 DESIGNED IN AUSTRALIA FOR WOMEN IN TRADES
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;